/**
 * The app shell: navy logo block, brand-dark topbar, grouped sidebar, scrolling main.
 * Structure and grouping are copied from the prototype (`.shell` markup + the `NAV`
 * array in docs/bosun-prototype.html).
 *
 * The sidebar counts (unread mail, my requirements) come from the API; until those
 * hooks are wired by /new-screen they are simply absent rather than faked.
 */

import { NavLink, Outlet, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTheme } from './ThemeProvider'
import { useSession } from './SessionProvider'
import { useActiveEnquiry } from './ActiveEnquiryProvider'
import { useRealtime } from '../lib/api/useRealtime'
import { flowLinkTarget, resolveActiveEnquiryId } from '../lib/activeEnquiry'
import { signOutSequence } from '../lib/api/signOut'
import { canSignOut, currentAuthProvider } from '../lib/api/authProvider'
import { refreshIdentity } from '../lib/api/identityRefresh'
import { useClerkSignOut } from './ClerkIdentityBridge'

interface NavGroup {
  group: string
  links: Array<{ to: string; icon: string; label: string }>
}

/** Mirrors the prototype's NAV array; `:id` routes carry the last enquiry actually opened. */
export const NAV: NavGroup[] = [
  {
    group: 'Workspace',
    links: [
      { to: '/mail', icon: '✉️', label: 'Mail' },
      { to: '/requirements', icon: '🗂️', label: 'Requirements' },
    ],
  },
  {
    group: 'Sourcing',
    links: [
      { to: '/rfq', icon: '📝', label: 'RFQ Builder' },
      { to: '/suppliers', icon: '🏭', label: 'Supplier Quotations' },
      { to: '/compare', icon: '⚖️', label: 'Quote Comparison' },
    ],
  },
  {
    group: 'Customer',
    links: [
      { to: '/quote', icon: '💵', label: 'Customer Quotation' },
      { to: '/revisions', icon: '✏️', label: 'Revisions' },
    ],
  },
  {
    group: 'Insight',
    links: [
      { to: '/analytics', icon: '📊', label: 'Analytics' },
      { to: '/settings', icon: '⚙️', label: 'Settings' },
    ],
  },
]

/** Flow screens are per-enquiry; the nav link carries the active id. */
const FLOW_ROUTES = new Set(['/rfq', '/suppliers', '/compare', '/quote', '/revisions'])

/** Typing "f" in a search box must not fling the user into focus mode. */
function isTypingInto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

export function Layout() {
  const { theme, toggleTheme } = useTheme()
  const { user, roleLabel, identity } = useSession()
  // Via the bridge, NOT `useClerk()`: that hook throws outside a `<ClerkProvider>`, and the
  // keyless dev path (no `VITE_CLERK_PUBLISHABLE_KEY`) renders this whole tree without one. The
  // sign-out BUTTON was already gated on `identity`, but the hook call was not — so every route
  // crashed before it could render. See `ClerkIdentityBridge`'s header for the rule.
  const signOut = useClerkSignOut()
  const queryClient = useQueryClient()
  const { lastEnquiryId, setLastEnquiryId } = useActiveEnquiry()
  const params = useParams()
  const [navHidden, setNavHidden] = useState(false)

  // ONE subscription for the whole app: `Layout` renders once per session (child routes swap
  // inside `<Outlet/>` below), so mounting it here — rather than in any individual screen — is
  // what guarantees the four realtime events are never subscribed to twice. See
  // `lib/api/useRealtime.ts` for what it does and does not do (mock does not emit; polling is
  // still the guarantee, not this).
  useRealtime()
  /**
   * Focus mode drops the nav AND the topbar so a dense screen — the RFQ table, the comparison
   * workbench, the mail three-pane — gets the whole viewport. Held here rather than per
   * screen so it survives navigation: a purchaser who turned it on for triage keeps it when
   * they move to comparison.
   */
  const [focusMode, setFocusMode] = useState(false)

  const activeId = resolveActiveEnquiryId(params.id, lastEnquiryId)

  // Remember every enquiry actually navigated to (a real `:id` in the URL) so the sidebar's
  // flow links can follow it from screens that have no id of their own (`/mail`,
  // `/requirements`, ...). Never seeded with a fabricated id — see `lib/activeEnquiry.ts`.
  useEffect(() => {
    if (params.id) setLastEnquiryId(params.id)
  }, [params.id, setLastEnquiryId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingInto(e.target)) return

      // Cmd/Ctrl + . toggles; Escape only ever leaves, so the way out is unconditional.
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault()
        setFocusMode((v) => !v)
      } else if (e.key === 'Escape' && focusMode) {
        setFocusMode(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [focusMode])

  return (
    <div className={`shell${navHidden && !focusMode ? ' nav-collapsed' : ''}${focusMode ? ' focus' : ''}`}>
      {/* The only way back when the topbar is gone — plus Escape. */}
      {focusMode ? (
        <button className="focus-exit" onClick={() => setFocusMode(false)} title="Leave focus mode (Esc)">
          ◱ Exit focus · Esc
        </button>
      ) : null}

      <div className="logo">
        <span className="mark">⚓</span> Waves
      </div>

      <div className="topbar">
        <button className="tbtn" onClick={() => setNavHidden((v) => !v)} title="Hide / show menu">
          ☰
        </button>
        <div className="crumb">
          {activeId ? (
            <>
              Following: <b>{activeId}</b>
            </>
          ) : (
            <span title="Open a requirement from the board to start a flow">
              Following: <b>— none opened yet</b>
            </span>
          )}
        </div>
        <div className="spacer" />
        <button
          className="tbtn"
          onClick={() => setFocusMode(true)}
          title="Focus mode — hide the chrome (⌘/Ctrl + .)"
        >
          ◱ Focus
        </button>
        <button className="tbtn" onClick={toggleTheme}>
          {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
        </button>
        {/*
          `identity` (who Clerk signed in) and `user` (who Frappe is acting as) are two different
          facts, and showing both side by side was right when `user` was the seed-data stub
          ('Akhil'): the pair told you the two did not agree.

          They agree now — `SessionProvider` reads `auth.get_current_user`, so both are the same
          address and the header printed it TWICE (reported 2026-08-11 with a screenshot:
          "akhilnair43@gmail.com   akhilnair43@gmail.com · Purchase Manager"). Repeating a value
          says nothing; the pair is only worth showing when it DISAGREES, which is the case that
          matters — a Clerk sign-in mapped to the wrong Frappe user. So the Clerk chip renders
          only then, and says so.
        */}
        {identity && signOut && identity.email !== user && (
          <span
            className="who"
            style={{ color: 'var(--warn)' }}
            title={`Signed in to Waves via Clerk as ${identity.email}, but acting as the Frappe user ${user}`}
          >
            ⚠ Clerk: {identity.email}
          </span>
        )}
        <span className="who" title="Acting as (stub — matched against Frappe records, not Clerk)">
          {user} · {roleLabel}
        </span>
        {/* Not Clerk's <UserButton>: its built-in sign-out cannot be reordered, and Frappe's logout
            must run first while the sid cookie still authorises it — see lib/api/signOut.ts. */}
        {canSignOut(currentAuthProvider(), identity, signOut) && (
          <button
            className="tbtn"
            title="Sign out of Waves"
            /* Under the `frappe` provider there is no Clerk session to end — `signOutSequence`
               still runs Frappe's logout first, and dropping the cache returns the login gate. */
            onClick={() => void signOutSequence(signOut ?? (() => Promise.resolve()), () => void refreshIdentity(queryClient))}
          >
            Sign out
          </button>
        )}
      </div>

      <nav className="nav">
        {NAV.map((group) => (
          <div key={group.group}>
            <div className="grp">{group.group}</div>
            {group.links.map((link) => (
              <NavLink
                key={link.to}
                to={FLOW_ROUTES.has(link.to) ? flowLinkTarget(link.to, activeId) : link.to}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                <span className="ico">{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <main className="main">
        <Outlet />
      </main>
    </div>
  )
}
